import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { debounce } from 'lodash'
import { dropDownStore, type ITabItem } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Message } from 'entities/Message/model/Message'
import { Contact } from 'entities/Contacts/model/Contact'
import { type IContactAll, type IContactIntegrationAll } from 'entities/Contacts'
import { ContactIntegrationBase } from 'entities/Contacts/model/Integrations/ContactIntegrationBase'
import type { IParamsCreateContact } from 'entities/Contacts/api/types'
import { ResultsContactsStore } from 'pages/conversations/pages/search/ui/Results/store/ResultsContactsStore'
import { ResultsMessagesStore } from 'pages/conversations/pages/search/ui/Results/store/ResultsMessagesStore'
import { ResultsKeyboardNavigationStore } from 'pages/conversations/pages/search/ui/Results/store/ResultsKeyboardNavigationStore'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import {
  getSearchResultsSessionCache,
  setSearchResultsSessionCache,
} from 'pages/conversations/pages/search/store/searchResultsSessionCache'
import { type ITabsResultsKeys } from './types'

export class ResultsStore {
  private _tabsMap: Map<ITabsResultsKeys, ITabItem<ITabsResultsKeys>> = new Map([
    [
      'top_results',
      {
        key: 'top_results',
        name: 'Top results',
      },
    ],
    [
      'contacts',
      {
        key: 'contacts',
        name: 'Contacts',
        count: 0,
      },
    ],
    [
      'messages',
      {
        key: 'messages',
        name: 'Messages',
        count: 0,
      },
    ],
  ])
  private _activeTab: ITabsResultsKeys = 'top_results'
  readonly resultsContactsStore: ResultsContactsStore
  readonly resultsMessagesStore: ResultsMessagesStore
  private _search = ''
  private _loadingCreating = false
  private _visibleUI = false
  private _paramsCreateContact = {
    forLink: true,
    fromConversation: true,
  }
  private _disposeEventSearch: IReactionDisposer | null = null
  debounceEventSearch: ReturnType<typeof debounce>
  readonly keyboardNavStore: ResultsKeyboardNavigationStore

  constructor(private _entities: IEntitiesStore) {
    this.keyboardNavStore = new ResultsKeyboardNavigationStore()

    makeAutoObservable(this)

    this.resultsContactsStore = new ResultsContactsStore(this._entities, {
      onCount: (value: number) => {
        this.updateCountTab('contacts', value)
      },
    })
    this.resultsMessagesStore = new ResultsMessagesStore(this._entities, {
      onCount: (value: number) => {
        this.updateCountTab('messages', value)
      },
    })
    this.debounceEventSearch = debounce(this._setSearchEvent, 500)
  }

  private _setSearchEvent = () => {
    logV2SearchEvent(
      'Search Page Search Performed',
      {
        event_id: 'search_page_search_performed',
        SearchRequest: this._search,
        SearchResult: this.count ? 'Results Found' : 'No results',
      },
      this._entities
    )
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  saveToSessionCache = () => {
    if (this._search.length < 3) return
    if (this.loading) return

    setSearchResultsSessionCache({
      search: this._search,
      contacts: this.resultsContactsStore.snapshot(),
      messages: this.resultsMessagesStore.snapshot(),
    })
  }

  tryRestoreFromSessionCache = (search: string): boolean => {
    const cacheState = getSearchResultsSessionCache(search)
    if (!cacheState) return false

    this._search = search
    this.keyboardNavStore.reset()

    this.resultsContactsStore.restore(cacheState.contacts)
    this.resultsMessagesStore.restore(cacheState.messages)

    this.updateCountTab('contacts', this.resultsContactsStore.count)
    this.updateCountTab('messages', this.resultsMessagesStore.count)
    this.presetActiveTab()

    return true
  }

  loadAll = (search: string) => {
    this._search = search
    this.keyboardNavStore.reset()
    this._resetActiveTab()

    if (search.length >= 3) {
      this.resultsContactsStore.fetchContacts(search)
      this.resultsMessagesStore.fetchMessages({ search: search })
    }
  }

  private _resetActiveTab = () => {
    if (this._activeTab === 'top_results') return

    this._activeTab = 'top_results'

    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.has('tab')) {
      urlParams.delete('tab')
      uiStore.changeQueryRoute(urlParams.toString())
    }
  }

  presetActiveTab = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const paramsTab = urlParams.get('tab')

    if (!paramsTab) return

    const tabKey = this._tabsMap.keys().find((key) => key === paramsTab)

    if (tabKey) {
      this._activeTab = tabKey
    }
  }

  setActiveTab = (key: ITabsResultsKeys) => {
    const urlParams = new URLSearchParams(window.location.search)

    urlParams.set('tab', key)

    uiStore.changeQueryRoute(urlParams.toString())

    this._activeTab = key
    this.keyboardNavStore.setHighlightedIndex(null)
  }

  init = () => {
    this.resultsContactsStore.init()
    this.resultsMessagesStore.init()
    this.reactionEventSearch()
  }

  dispose = () => {
    this.updateCountTab('contacts', 0)
    this.updateCountTab('messages', 0)
    this._activeTab = 'top_results'
    this._search = ''
    this._loadingCreating = false
    this._paramsCreateContact = {
      forLink: true,
      fromConversation: true,
    }

    this.resultsContactsStore.dispose()
    this.resultsMessagesStore.dispose()
    this._disposeEventSearch?.()
    this.debounceEventSearch.cancel()
    this.keyboardNavStore.reset()
  }

  reactionEventSearch = () => {
    this._disposeEventSearch?.()
    this._disposeEventSearch = reaction(
      () => ({
        search: this._search,
        loading: this.loading,
      }),
      ({ search, loading }) => {
        if (search.length < 3 || loading) {
          this.debounceEventSearch?.cancel?.()

          return
        }

        this.debounceEventSearch()
      }
    )
  }

  updateCountTab = (key: ITabsResultsKeys, count: number) => {
    const tab = this._tabsMap.get(key)

    if (tab) {
      this._tabsMap.set(key, {
        ...tab,
        count: count,
      })
    }
  }

  getUrlParams = (item: Message | IContactAll) => {
    const urlParams = new URLSearchParams(window.location.search)

    if (item instanceof Contact) {
      urlParams.set('contactId', String(item.id))
    }

    if (item instanceof Message) {
      urlParams.set('contactId', String(item.contact_id))
      urlParams.set('messageId', String(item.id))
    }

    return urlParams
  }

  handleGoToProfileNewConversation = (item: Contact) => {
    const urlParams = this.getUrlParams(item)

    urlParams.set('hide_filter', '1')

    uiStore.changeRoute({
      path: `/${ConversationsPath.searchProfile}/0`,
      query: urlParams.toString(),
    })
  }

  handleGoToProfile = async (item: Message | IContactAll) => {
    let urlParams = this.getUrlParams(item)

    if (item instanceof ContactIntegrationBase) {
      urlParams = await this.createContactFromContactIntegration(item)
    }

    if (urlParams.size) {
      if (this._visibleUI) {
        if (item instanceof Message) {
          uiStore.changeRoute({
            path: `/${ConversationsPath.searchProfile}/${item.conversation_id}`,
            query: urlParams.toString(),
          })
        }

        if (item instanceof Contact) {
          uiStore.changeRoute({
            path: `/${ConversationsPath.searchProfile}`,
            query: urlParams.toString(),
          })
        }

        if (item instanceof ContactIntegrationBase) {
          urlParams.set('create_integration_contact', '1')
          urlParams.set('hide_filter', '1')

          uiStore.changeRoute({
            path: `/${ConversationsPath.searchProfile}/0`,
            query: urlParams.toString(),
          })
        }
      }
    }
  }

  createContactFromContactIntegration = async (
    item: IContactIntegrationAll
  ): Promise<URLSearchParams> => {
    return new Promise(async (resolve) => {
      try {
        runInAction(() => {
          this._loadingCreating = true
        })

        if (item.phoneCount > 1) {
          this._entities.contactsStore.contactChoosePhoneModalStore.openModal({
            contact: item,
            callback: (item) => {
              const urlParams = this.getUrlParams(item)

              resolve(urlParams)
            },
            onClose: () => {
              resolve(new URLSearchParams())
            },
            params: this._paramsCreateContact,
          })

          runInAction(() => {
            this._loadingCreating = false
            dropDownStore.allHide()
          })
          return
        }

        const params: IParamsCreateContact = {
          number: item.phone || item.mobile_phone || item.phoneInfo,
          forLink: this._paramsCreateContact.forLink,
          fromConversation: this._paramsCreateContact.fromConversation,
          ...item.params,
        }

        const integration = this._entities.integrationsStore.getIntegration(item.integration_type)

        params.integration_id = integration?.id

        const contact = await this._entities.contactsStore.createContact(params)

        if (contact) {
          const urlParams = this.getUrlParams(contact)

          resolve(urlParams)
        } else {
          resolve(new URLSearchParams())
        }
      } catch (e) {
        logger.error(e)

        resolve(new URLSearchParams())
      } finally {
        runInAction(() => {
          this._loadingCreating = false
        })
      }
    })
  }

  get tabs() {
    return Array.from(this._tabsMap.values())
  }

  get activeTab() {
    return this._activeTab
  }

  get count() {
    return this.resultsContactsStore.count + this.resultsMessagesStore.count
  }

  get search() {
    return this._search
  }

  get loadingCreating() {
    return this._loadingCreating
  }

  get loading() {
    return this.resultsMessagesStore.loading || this.resultsContactsStore.loading
  }

  get currentTabItems(): (Message | IContactAll)[] {
    if (this._activeTab === 'top_results') {
      return [...this.resultsContactsStore.itemsTopResult, ...this.resultsMessagesStore.items]
    }

    if (this._activeTab === 'contacts') {
      return this.resultsContactsStore.activeContactsByIntegration?.store.items ?? []
    }

    if (this._activeTab === 'messages') {
      return this.resultsMessagesStore.itemsAll
    }

    return []
  }

  get currentTabItemCount() {
    return this.currentTabItems.length
  }
}
